import 'floating-vue/dist/style.css'
import './assets/styles/main.css'

import { createApp } from 'vue'
import { createHead } from '@unhead/vue/client'
import { createPinia } from 'pinia'
import HoneybadgerVue from '@honeybadger-io/vue'
import FloatingVue from 'floating-vue'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { useNotificationStore } from '@/stores/notification'

import App from './App.vue'
import router from './router'
import 'flowbite'
import { useHotjarStore } from '@/stores/hotjar'
import { useGoogleTagManagerStore } from '@/stores/google-tag-manager'
import { getLocalVersion } from '@/utils/version-checker'
import { maybeApplyNoiseTags, hasTag, shouldDropNoise, NOISE_TAG } from '@/utils/honeybadger'

const HoneybadgerVuePlugin = HoneybadgerVue.default || HoneybadgerVue

const app = createApp(App)
const head = createHead()
const pinia = createPinia()
const config = {
  apiKey: import.meta.env.VITE_HONEYBADGER_API_KEY,
  environment: import.meta.env.VITE_HONEYBADGER_ENVIRONMENT,
  revision: import.meta.env.VITE_HONEYBADGER_REVISION
}

pinia.use(piniaPluginPersistedstate)

app.use(pinia)
app.use(router)
app.use(head)
app.use(HoneybadgerVuePlugin, config)
app.use(FloatingVue)

app.$honeybadger.beforeNotify((notice) => {
  const message = notice.context?.errorMessage || notice.message || ''
  const ignoredErrorPatterns = (import.meta.env.VITE_IGNORED_ERROR_PATTERNS || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  // Custom logic for triggering app reload prompt
  const reloadTriggers = [
    'is not a valid JavaScript MIME type',
    'Unable to preload CSS',
    'Failed to fetch dynamically imported module'
  ]

  notice.context = {
    ...(notice.context || {}),
    assetsVersion: getLocalVersion()
  }

  maybeApplyNoiseTags(notice)

  if (shouldDropNoise() && hasTag(notice, NOISE_TAG)) return false

  if (reloadTriggers.some((trigger) => message.includes(trigger))) {
    const notificationStore = useNotificationStore()
    notificationStore.notifyReload = true
    return false
  }

  if (message && message.includes('Failed to load Stripe.js')) {
    window.location.reload()
    return false
  }

  // Handle infinite recursion errors
  if (message.includes('Maximum call stack size exceeded')) {
    console.error('Infinite recursion detected, attempting to recover...')

    // Clear all intervals and timeouts
    const maxIntervalId = window.setInterval(() => {}, 999999)
    for (let i = 1; i <= maxIntervalId; i++) {
      window.clearInterval(i)
    }

    // Clear all timeouts
    const maxTimeoutId = window.setTimeout(() => {}, 999999)
    for (let i = 1; i <= maxTimeoutId; i++) {
      window.clearTimeout(i)
    }

    // Force reload after a short delay
    setTimeout(() => {
      console.log('Reloading due to infinite recursion...')
      window.location.reload()
    }, 1000)
    return false
  }

  // Check against environment-driven ignore list
  const shouldIgnore = ignoredErrorPatterns.some((pattern) => message.includes(pattern))

  return !shouldIgnore
})

window.gmo = window.gmo || {}

useGoogleTagManagerStore().init()
useHotjarStore().init()

app.mount('#app')
